import { useState } from 'react';
import { api, can, getUser } from '../lib/api';
import { date, shortList } from '../lib/format';
import {
  Card, Empty, ErrorNote, Field, Loading, Modal, PageHead, Pill, SecretReveal, useResource, useToast,
} from '../components/ui';
import { RowActions } from '../components/RowActions';
import '../styles/children.css';

/**
 * CM-3 Collection points.
 *
 * A collection point is a named place consent is captured, bound to one notice
 * and a fixed set of purposes. Its key is publishable — it ships inside a web
 * page — so it can only submit consent, never read a record.
 */

interface CollectionPoint {
  id: string;
  name: string;
  channel: string;
  noticeId: string;
  purposeIds: string[];
  apiKeyPrefix: string;
  status: 'active' | 'disabled';
  createdAt: string;
  forwardUrl: string | null;
  childSettings?: ChildSettings;
}

/** S.9 settings: whether the page asks about age, who records the Rule 10/11 check. */
interface ChildSettings { ageQuestion?: 'off' | 'ask'; checkBy?: 'operator' | 'fiduciary'; disability?: boolean }

interface Purpose { id: string; status: string; current: { name: string } }
interface Notice { id: string; key: string; versions: Array<{ version: number; status: string; title: string }> }

const CHANNELS = ['web', 'app', 'cookie', 'api', 'paper', 'kiosk', 'offline', 'callcentre', 'ivr', 'email', 'sms', 'whatsapp'] as const;

const CHANNEL_LABEL: Record<string, string> = { web: 'Website', app: 'Mobile app', cookie: 'Cookie banner', api: 'Integration', paper: 'Paper form', kiosk: 'Kiosk', offline: 'In person (branch, event)', callcentre: 'Call centre (agent)', ivr: 'Phone (IVR)', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp' };

export default function CollectionPoints() {
  const writable = can(getUser(), 'collection_points:edit');
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<{ name: string; key: string } | null>(null);
  const [snippetFor, setSnippetFor] = useState<CollectionPoint | null>(null);
  const [childFor, setChildFor] = useState<CollectionPoint | null>(null);

  const points = useResource<{ collectionPoints: CollectionPoint[] }>(() => api.get('/v1/collection-points'));
  const purposes = useResource<{ purposes: Purpose[] }>(() => api.get('/v1/purposes?status=active'));
  const notices = useResource<{ notices: Notice[] }>(() => api.get('/v1/notices'));

  const purposeName = (id: string) =>
    purposes.data?.purposes.find((p) => p.id === id)?.current.name ?? id;

  async function toggle(cp: CollectionPoint) {
    try {
      await api.post(`/v1/collection-points/${cp.id}/status`, {
        status: cp.status === 'active' ? 'disabled' : 'active',
      });
      points.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  async function rotate(cp: CollectionPoint) {
    try {
      const res = await api.post<{ apiKey: string }>(`/v1/collection-points/${cp.id}/rotate-key`);
      setSecret({ name: cp.name, key: res.apiKey });
      points.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <>
      <PageHead
        title="Collection points"
      >
        {writable && <button className="primary" onClick={() => setCreating(true)}>New collection point</button>}
      </PageHead>

      <ErrorNote error={points.error} />
      {points.loading && !points.data && <Loading what="Loading collection points" />}

      {secret && (
        <SecretReveal
          label={`Key for "${secret.name}"`}
          secret={secret.key}
          note="Publishable key. Grants submit access for this collection point only; no read access."
        />
      )}

      {points.data && (points.data.collectionPoints.length === 0 ? (
        <Empty>No collection points.</Empty>
      ) : (
        <Card>
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Channel</th>
                  <th>Purposes offered</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {points.data.collectionPoints.map((cp) => (
                  <tr key={cp.id}>
                    <td>
                      <strong>{cp.name}</strong>
                    </td>
                    <td className="text-list">{CHANNEL_LABEL[cp.channel] ?? cp.channel}{cp.childSettings?.ageQuestion === 'ask' ? ' · Asks age' : ''}</td>
                    <td className="text-list">{shortList(cp.purposeIds.map(purposeName), 2)}</td>
                    <td>{cp.status === 'active' ? <span className="muted small">Active</span> : <Pill>Disabled</Pill>}</td>
                    <td className="right" style={{ whiteSpace: 'nowrap' }}>
                      <RowActions
                        primary={<button className="small" onClick={() => setSnippetFor(cp)}>Embed code</button>}
                        more={writable ? [
                          { label: 'Children', onClick: () => setChildFor(cp) },
                          { label: `Rotate key (${cp.apiKeyPrefix}…)`, onClick: () => rotate(cp) },
                          { label: cp.status === 'active' ? 'Disable' : 'Enable', onClick: () => toggle(cp), danger: cp.status === 'active' },
                        ] : []}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {creating && (
        <NewPoint
          purposes={purposes.data?.purposes ?? []}
          notices={notices.data?.notices ?? []}
          onClose={() => setCreating(false)}
          onCreated={(cp, key) => {
            setCreating(false);
            setSecret({ name: cp.name, key });
            points.reload();
          }}
        />
      )}

      {snippetFor && <Snippet cp={snippetFor} onClose={() => setSnippetFor(null)} />}

      {childFor && (
        <ChildSettingsDialog
          cp={childFor}
          onClose={() => setChildFor(null)}
          onSaved={() => { setChildFor(null); toast('Saved.', 'ok'); points.reload(); }}
        />
      )}
    </>
  );
}

function NewPoint({
  purposes, notices, onClose, onCreated,
}: {
  purposes: Purpose[];
  notices: Notice[];
  onClose: () => void;
  onCreated: (cp: CollectionPoint, key: string) => void;
}) {
  const publishable = notices.filter((n) => n.versions.some((v) => v.status === 'published'));
  const [name, setName] = useState('');
  const [channel, setChannel] = useState<(typeof CHANNELS)[number]>('web');
  const [noticeId, setNoticeId] = useState(publishable[0]?.id ?? '');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ collectionPoint: CollectionPoint; apiKey: string }>(
        '/v1/collection-points',
        { name, channel, noticeId, purposeIds: selected },
      );
      onCreated(res.collectionPoint, res.apiKey);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New collection point"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={create} disabled={busy || !name || !noticeId || !selected.length}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Name">
          <input type="text" placeholder="Website signup" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Channel">
          <select value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
            {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Notice">
        {publishable.length === 0 ? (
          <div className="note warn">No published notice. A collection point requires a published notice.</div>
        ) : (
          <select value={noticeId} onChange={(e) => setNoticeId(e.target.value)}>
            {publishable.map((n) => (
              <option key={n.id} value={n.id}>{n.versions[0]?.title ?? n.key}</option>
            ))}
          </select>
        )}
      </Field>

      <Field label="Purposes" help="Each purpose is presented as a separate, unticked option.">
        {purposes.map((p) => (
          <label key={p.id} style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '5px 0', fontWeight: 400 }}>
            <input
              type="checkbox" style={{ width: 'auto' }}
              checked={selected.includes(p.id)}
              onChange={(e) => setSelected((s) => (e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id)))}
            />
            {p.current.name}
          </label>
        ))}
      </Field>

      <ErrorNote error={error} />
    </Modal>
  );
}

function Snippet({ cp, onClose }: { cp: CollectionPoint; onClose: () => void }) {
  const origin = window.location.origin;
  const code = `<div id="wekalp-consent"></div>
<script
  src="${origin}/widget.js"
  data-api="${origin}"
  data-key="${cp.apiKeyPrefix}.YOUR_KEY_SECRET"
  data-target="#wekalp-consent"
  data-identifier-field="#email"
  data-identifier-type="email"
  data-lang="en"></script>`;

  return (
    <Modal
      wide
      title={`Embed · ${cp.name}`}
      onClose={onClose}
      footer={<button onClick={onClose}>Close</button>}
    >
      <pre className="mono" style={{ background: 'var(--surface-tint)', padding: 14, borderRadius: 8, overflowX: 'auto' }}>
        {code}
      </pre>
      <div className="note">
        Replace <code>YOUR_KEY_SECRET</code> with the key issued at creation. Only the hash is
        stored; rotate the key to issue a new one.
      </div>
      {cp.channel === 'cookie' && (
        <>
          <h2 className="section">Through Google Tag Manager</h2>
          <pre className="mono" style={{ background: 'var(--surface-tint)', padding: 14, borderRadius: 8, overflowX: 'auto' }}>
{`<!-- Custom HTML tag, trigger: Consent Initialization - All Pages -->
<script
  src="${origin}/cookies.js"
  data-api="${origin}"
  data-key="${cp.apiKeyPrefix}.YOUR_KEY_SECRET"
  data-consent-mode="google"
  data-lang="en"></script>`}
          </pre>
          <div className="note">
            Sets Google Consent Mode v2 to denied on load and updates it from the visitor's choices. Analytics categories map to analytics_storage; marketing, advertising and social to the three ad types; functional to functionality and personalisation storage. Each change also sends the event <code>wekalp_consent_update</code> for GTM triggers.
          </div>
        </>
      )}
      <HostedPage cp={cp} />
      <h2 className="section">Deferred identifier</h2>
      <pre className="mono" style={{ background: 'var(--surface-tint)', padding: 14, borderRadius: 8, overflowX: 'auto' }}>
{`// choices are held client-side until the identifier is known
await createAccount(form);
await WekalpConsent.submit({ identifier: form.email, identifierType: 'email' });`}
      </pre>
    </Modal>
  );
}

/** Link for forms that cannot run a script (Google Forms): consent first, then forward. */
function HostedPage({ cp }: { cp: CollectionPoint }) {
  const toast = useToast();
  const [url, setUrl] = useState(cp.forwardUrl ?? '');
  const [saved, setSaved] = useState(cp.forwardUrl ?? '');
  const [busy, setBusy] = useState(false);
  const link = `${window.location.origin}/consent/?k=${cp.apiKeyPrefix}.YOUR_KEY_SECRET&lang=en`;

  async function save() {
    setBusy(true);
    try {
      await api.put(`/v1/collection-points/${cp.id}/forward-url`, { forwardUrl: url.trim() || null });
      setSaved(url.trim());
      toast('Forward address saved.', 'ok');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2 className="section">Hosted consent page</h2>
      <pre className="mono" style={{ background: 'var(--surface-tint)', padding: 14, borderRadius: 8, overflowX: 'auto' }}>{link}</pre>
      <Field label="Send the visitor on to" help="https only. {ref} is replaced by the receipt reference, for example a Google Form prefill link.">
        <input type="url" value={url} placeholder="https://docs.google.com/forms/d/e/…/viewform?entry.123={ref}" onChange={(e) => setUrl(e.target.value)} />
      </Field>
      <button className="small" onClick={save} disabled={busy || url.trim() === saved}>{busy ? 'Saving…' : 'Save forward address'}</button>
    </>
  );
}

function ChildSettingsDialog({ cp, onClose, onSaved }: { cp: CollectionPoint; onClose: () => void; onSaved: () => void }) {
  const [ageQuestion, setAgeQuestion] = useState<'off' | 'ask'>(cp.childSettings?.ageQuestion ?? 'off');
  const [checkBy, setCheckBy] = useState<'operator' | 'fiduciary'>(cp.childSettings?.checkBy ?? 'operator');
  const [disability, setDisability] = useState<boolean>(cp.childSettings?.disability ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/v1/collection-points/${cp.id}/child-settings`, { ageQuestion, checkBy, disability });
      onSaved();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Children · ${cp.name}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      <Field label="Ask whether the consent is for a child">
        <select value={ageQuestion} onChange={(e) => setAgeQuestion(e.target.value as 'off' | 'ask')}>
          <option value="off">Do not ask</option>
          <option value="ask">Ask</option>
        </select>
      </Field>
      <Field label="Who records the guardian check">
        <select value={checkBy} onChange={(e) => setCheckBy(e.target.value as 'operator' | 'fiduciary')}>
          <option value="operator">A console user</option>
          <option value="fiduciary">Our own system over the API</option>
        </select>
      </Field>
      <label className="gc-check">
        <input type="checkbox" checked={disability} onChange={(e) => setDisability(e.target.checked)} />
        Accept lawful guardians of persons with disability
      </label>
      <ErrorNote error={error} />
    </Modal>
  );
}
